/**
 * PP Quiz (rediseño) — «Encuentra tu planta ideal»
 *
 * sections/pp-quiz-rediseno.liquid pinta las preguntas (ocultas) y este script:
 * - Las enseña de una en una. Con la primera respuesta empieza a pedir los datos
 *   de las plantas a sections/pp-quiz-datos-rediseno.liquid (Section Rendering
 *   API, 250 por página), así al terminar ya suelen estar.
 * - Puntúa con los datos reales de Shopify:
 *     · solo plantas con stock;
 *     · mascotas es obligatorio y nunca se relaja: apta = metacampo
 *       pet_friendly true, o etiqueta «Pet Friendly» sin un false explícito;
 *     · el resto de respuestas suma 1 punto por coincidencia. «Me da igual»
 *       (respuesta sin valores) no cuenta.
 *   Salen las de más puntos; a igualdad, en el orden de la colección.
 * - Plan B: si ninguna cumple todo, salen las más cercanas con aviso; si no hay
 *   ninguna (p. ej. aptas para mascotas sin stock), mensaje y enlaces.
 * - «Añadir» usa /cart/add.js y refresca el drawer como el resto de la tienda.
 * - En el editor de temas marca las respuestas cuyo valor no tiene ningún producto
 *   y enseña la pregunta del bloque que se selecciona.
 */
(function () {
  'use strict';

  if (customElements.get('pp-quiz')) return;

  var DATA_SECTION = 'pp-quiz-datos-rediseno';

  /* Textos por defecto: los mismos que los ajustes «Textos» de la sección.
     Solo se usan si un ajuste se deja vacío en el editor. */
  var DEFAULT_TEXTS = {
    progress: 'Pregunta [n] de [total]',
    loading: 'Buscando tus plantas…',
    resultsTitle: 'Estas son tus plantas ideales',
    match: 'Coincide en [n] de [total]',
    petTag: 'Apta para mascotas',
    added: '✓ Añadido',
    addError: 'No disponible',
  };

  /* Sustituye [n] y [total] */
  function fill(text, n, total) {
    return String(text).replace(/\[n\]/g, n).replace(/\[total\]/g, total);
  }

  function list(v) {
    if (v == null || v === '') return [];
    return (Array.isArray(v) ? v : [v]).map(norm).filter(Boolean);
  }

  function norm(s) {
    return String(s).trim().toLowerCase();
  }

  function isPetSafe(item) {
    return item.pm === true || (item.pt === 1 && item.pm !== false);
  }

  class PpQuiz extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ppQuizReady === '1') return;
      this.dataset.ppQuizReady = '1';

      this.steps = Array.prototype.slice.call(this.querySelectorAll('[data-pp-quiz-step]'));
      if (!this.steps.length) return; /* sin preguntas configuradas: no se enseña */

      this.limit = parseInt(this.dataset.limit, 10) || 6;
      this.texts = Object.assign({}, DEFAULT_TEXTS);
      try {
        var custom = JSON.parse(this.querySelector('[data-pp-quiz-texts]').textContent);
        Object.keys(custom).forEach(function (k) { if (custom[k]) this.texts[k] = custom[k]; }, this);
      } catch (e) { /* sin textos de la sección: valores por defecto */ }
      this.nav = this.querySelector('[data-pp-quiz-nav]');
      this.backBtn = this.querySelector('[data-pp-quiz-back]');
      this.progressEl = this.querySelector('[data-pp-quiz-progress]');
      this.results = this.querySelector('[data-pp-quiz-results]');
      this.resultsTitle = this.querySelector('[data-pp-quiz-results-title]');
      this.partialEl = this.querySelector('[data-pp-quiz-partial]');
      this.grid = this.querySelector('[data-pp-quiz-grid]');
      this.emptyEl = this.querySelector('[data-pp-quiz-empty]');
      this.errorEl = this.querySelector('[data-pp-quiz-error]');
      this.cardTpl = this.querySelector('template[data-pp-quiz-card]');

      this.answers = [];
      this.current = 0;

      this.addEventListener('click', this.onClick.bind(this));

      if (this.hasAttribute('data-design-mode')) this.setupEditor();

      this.showStep(0);
      this.hidden = false;
    }

    /* ---------------- Navegación ---------------- */

    onClick(e) {
      var answer = e.target.closest('[data-pp-quiz-answer]');
      if (answer) return this.choose(answer);
      if (e.target.closest('[data-pp-quiz-back]')) return this.back();
      if (e.target.closest('[data-pp-quiz-restart]')) return this.restart();
      var add = e.target.closest('[data-card-add]');
      if (add) return this.addToCart(add);
    }

    showStep(i) {
      this.current = i;
      this.steps.forEach(function (s, n) { s.hidden = n !== i; });
      this.results.hidden = true;
      this.nav.hidden = false;
      this.backBtn.hidden = i === 0;
      this.progressEl.textContent = fill(this.texts.progress, i + 1, this.steps.length);
    }

    choose(btn) {
      var step = btn.closest('[data-pp-quiz-step]');
      var i = this.steps.indexOf(step);
      if (i < 0) return;

      step.querySelectorAll('[data-pp-quiz-answer]').forEach(function (b) {
        b.classList.toggle('is-selected', b === btn);
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      });
      this.answers[i] = {
        field: btn.dataset.field || step.dataset.field,
        values: list((btn.dataset.values || '').split(',')),
      };

      this.loadData(); /* empieza a traer los datos en segundo plano */

      if (i < this.steps.length - 1) {
        this.showStep(i + 1);
      } else {
        this.showResults();
      }
    }

    back() {
      if (!this.results.hidden) return this.showStep(this.steps.length - 1);
      if (this.current > 0) this.showStep(this.current - 1);
    }

    restart() {
      this.answers = [];
      this.querySelectorAll('[data-pp-quiz-answer]').forEach(function (b) {
        b.classList.remove('is-selected');
        b.removeAttribute('aria-pressed');
      });
      this.showStep(0);
      this.scrollIntoViewIfNeeded();
    }

    scrollIntoViewIfNeeded() {
      var top = this.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight * 0.6) {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    /* ---------------- Datos ---------------- */

    dataUrl(page) {
      var base = this.dataset.dataUrl || window.location.pathname;
      return base + '?section_id=' + DATA_SECTION + '&page=' + page;
    }

    fetchPage(page) {
      return fetch(this.dataUrl(page), { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then(function (html) {
          var m = html.match(/data-pp-quiz-data>([\s\S]*?)<\/script>/);
          if (!m) throw new Error('sin datos');
          return JSON.parse(m[1]);
        });
    }

    loadData() {
      if (this.dataPromise) return this.dataPromise;
      var self = this;
      this.dataPromise = this.fetchPage(1).then(function (first) {
        var rest = [];
        for (var p = 2; p <= (first.pages || 1); p++) rest.push(self.fetchPage(p));
        return Promise.all(rest).then(function (pages) {
          return pages.reduce(function (all, pg) { return all.concat(pg.items || []); }, first.items || []);
        });
      });
      /* si falla, permite reintentar en el siguiente intento */
      this.dataPromise.catch(function () { self.dataPromise = null; });
      return this.dataPromise;
    }

    /* ---------------- Resultados ---------------- */

    showResults() {
      var self = this;
      this.steps.forEach(function (s) { s.hidden = true; });
      this.backBtn.hidden = false;
      this.progressEl.textContent = '';
      this.results.hidden = false;
      this.results.classList.add('is-loading');
      this.resultsTitle.textContent = this.texts.loading;
      this.partialEl.hidden = true;
      this.emptyEl.hidden = true;
      this.errorEl.hidden = true;
      this.grid.innerHTML = '';
      this.scrollIntoViewIfNeeded();

      this.loadData()
        .then(function (items) { self.render(self.pick(items)); })
        .catch(function (err) {
          console.error('[pp-quiz] no se pudieron cargar los datos:', err);
          self.results.classList.remove('is-loading');
          self.resultsTitle.textContent = '';
          self.errorEl.hidden = false;
        });
    }

    pick(items) {
      var needsPet = this.answers.some(function (a) { return a && a.field === 'pet' && a.values.length; });
      var criteria = this.answers.filter(function (a) { return a && a.field !== 'pet' && a.values.length; });

      var scored = [];
      items.forEach(function (item, index) {
        if (item.a !== 1 || !item.v) return;
        if (needsPet && !isPetSafe(item)) return;
        var score = 0;
        criteria.forEach(function (c) {
          var have = list(item[c.field]);
          if (c.values.some(function (v) { return have.indexOf(v) !== -1; })) score++;
        });
        scored.push({ item: item, score: score, index: index });
      });

      scored.sort(function (a, b) { return b.score - a.score || a.index - b.index; });
      return { top: scored.slice(0, this.limit), max: criteria.length, pet: needsPet };
    }

    render(res) {
      this.results.classList.remove('is-loading');

      if (!res.top.length) {
        this.resultsTitle.textContent = '';
        this.emptyEl.hidden = false;
        return;
      }

      var best = res.top[0].score;
      this.resultsTitle.textContent = this.texts.resultsTitle;
      this.partialEl.hidden = !(res.max > 0 && best < res.max);

      var frag = document.createDocumentFragment();
      res.top.forEach(function (r) {
        var it = r.item;
        var card = this.cardTpl.content.firstElementChild.cloneNode(true);
        card.querySelectorAll('[data-card-link]').forEach(function (a) { a.href = it.u; });
        var img = card.querySelector('[data-card-img]');
        if (it.i) { img.src = it.i; img.alt = it.t; } else { img.remove(); }
        card.querySelector('[data-card-title]').textContent = it.t;
        card.querySelector('[data-card-price]').textContent = it.p;
        card.querySelector('[data-card-compare]').textContent = it.c || '';

        var tags = [];
        if (res.max > 0) tags.push(fill(this.texts.match, r.score, res.max));
        if (res.pet) tags.push(this.texts.petTag);
        card.querySelector('[data-card-match]').textContent = tags.join(' · ');

        var add = card.querySelector('[data-card-add]');
        add.dataset.variantId = it.v;
        add.setAttribute('aria-label', add.textContent.trim() + ' ' + it.t);
        frag.appendChild(card);
      }, this);
      this.grid.appendChild(frag);
    }

    /* ---------------- Cesta ---------------- */

    addToCart(btn) {
      if (btn.disabled) return;
      var label = btn.textContent;
      var texts = this.texts;
      btn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: parseInt(btn.dataset.variantId, 10), quantity: 1 }] }),
      })
        .then(function (r) {
          return r.json().then(function (body) { return { ok: r.ok, body: body }; });
        })
        .then(function (res) {
          if (!res.ok) throw new Error((res.body && (res.body.description || res.body.message)) || 'error');
          btn.textContent = texts.added;
          if (typeof window.ppRefreshCartDrawer === 'function') {
            try {
              var p = window.ppRefreshCartDrawer({ open: true });
              if (p && typeof p.catch === 'function') p.catch(function () {});
            } catch (e) { /* el drawer se actualizará en la próxima carga */ }
          }
          return fetch('/cart.js', { credentials: 'same-origin' })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              document.querySelectorAll('.pp-header__cart-count, [data-cart-count]').forEach(function (el) {
                el.textContent = cart.item_count;
              });
              /* Misma forma que el evento de pp-product-main.js: hay listeners que leen data.sections */
              document.dispatchEvent(new CustomEvent('cart:update', {
                bubbles: true,
                detail: { resource: cart, sourceId: 'pp-quiz-rediseno', data: { source: 'pp-quiz-rediseno', sections: {} } },
              }));
            })
            .catch(function () {});
        })
        .catch(function (err) {
          console.error('[pp-quiz] no se pudo añadir:', err);
          btn.textContent = texts.addError;
        })
        .then(function () {
          setTimeout(function () {
            btn.textContent = label;
            btn.disabled = false;
          }, 1800);
        });
    }

    /* ---------------- Editor de temas ---------------- */

    setupEditor() {
      var self = this;

      /* Avisa de respuestas cuyo valor no tiene ningún producto (erratas) */
      this.loadData().then(function (items) {
        self.querySelectorAll('[data-pp-quiz-answer]').forEach(function (btn) {
          var step = btn.closest('[data-pp-quiz-step]');
          var field = btn.dataset.field || step.dataset.field;
          var values = list((btn.dataset.values || '').split(','));
          var warn = btn.querySelector('[data-pp-quiz-warn]');
          if (!warn || field === 'pet' || !values.length) return;
          var missing = values.filter(function (v) {
            return !items.some(function (it) { return list(it[field]).indexOf(v) !== -1; });
          });
          warn.hidden = !missing.length;
          if (missing.length) warn.textContent = 'Sin productos: ' + missing.join(', ');
        });
      }).catch(function () {});

      /* Al seleccionar un bloque en el editor, enseña su pregunta */
      document.addEventListener('shopify:block:select', function (e) {
        if (!self.contains(e.target)) return;
        var step = e.target.closest('[data-pp-quiz-step]');
        var i = self.steps.indexOf(step);
        if (i >= 0) self.showStep(i);
      });
    }
  }

  customElements.define('pp-quiz', PpQuiz);
})();
